import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useCreateKitAssetsMutation } from '../../../../../redux/api/fymAPI';
import { CreateKitAssetItem, CreateKitAssetsResult } from '../../../../../redux/api/fymAPI.types';
import { useShowToast } from '../../../../../redux/slices/toasts/hooks';
import { kitRefusal } from './kitErrors';
import { assetMessages } from './assetMessages';
import { itemsCredits } from '../../studio/studioRules';
import { useVideoCreditGate } from '../../credits/useVideoCreditGate';
import { splitKitItemsForBalance } from '../../credits/splitKitItemsForBalance';

/** The element id of the song line on the song page; a `M24_NO_MASTER` refusal scrolls to it. */
export const SONG_FIELD_ANCHOR = 'release-song-field';

interface UseMakeKitAssetsProps {
  accountId: number;
  catalogItemUuid: string;
}

export interface MakeKitAssets {
  make: (items: CreateKitAssetItem[], batch: boolean) => Promise<void>;
  isMaking: boolean;
  /**
   * The videos of the last "Make the kit" that the balance could not pay (owner rule
   * 2026-10-06). The images went at once; these wait for credits. Empty when nothing waits.
   */
  skippedVideos: CreateKitAssetItem[];
  /** The credits that the skipped videos need together. */
  skippedCredits: number;
  /** The one click after a purchase: makes the skipped videos, or opens the packs when still short. */
  makeSkipped: () => void;
}

const skippedKey = (catalogItemUuid: string): string => `m24KitSkippedVideos:${catalogItemUuid}`;

/** The skipped videos survive the trip to Stripe and back (session storage of the tab). */
function readSkipped(catalogItemUuid: string): CreateKitAssetItem[] {
  try {
    const value: unknown = JSON.parse(window.sessionStorage.getItem(skippedKey(catalogItemUuid)) ?? '[]');
    return Array.isArray(value) ? (value as CreateKitAssetItem[]) : [];
  } catch {
    return [];
  }
}

function writeSkipped(catalogItemUuid: string, items: CreateKitAssetItem[]): void {
  try {
    if (items.length === 0) {
      window.sessionStorage.removeItem(skippedKey(catalogItemUuid));
    } else {
      window.sessionStorage.setItem(skippedKey(catalogItemUuid), JSON.stringify(items));
    }
  } catch {
    // No storage: the skipped videos live until the page closes.
  }
}

function scrollToSongField(): void {
  const field = document.getElementById(SONG_FIELD_ANCHOR);
  field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/** The toast for the videos that the server left out because the master is missing (review I5). */
function skippedMessage(result: CreateKitAssetsResult) {
  const skipped = result.skipped ?? [];
  if (skipped.length === 0) {
    return null;
  }
  return skipped.some((entry) => entry.reason === 'needsMaster')
    ? assetMessages.skippedNoMaster
    : assetMessages.skippedMasterProcessing;
}

/**
 * Posts kit items (`POST .../release-kits/:uuid/assets`). "Make it", "Try again" and "Make the N
 * missing" use it. The song page data refreshes through the cache tags. Busy themes (a second
 * click while the first set works) answer in `busy` or with 409 and show one toast. Before the
 * call, a balance (plan plus packs) below the cost of the items opens the credit packs (g8) or the
 * owner note (e4) at once and sends nothing (owner rule 2026-10-06). A 402 for video credits needs
 * nothing here either: the global upgrade modal opens the same packs on every screen. With no
 * master, "Make the kit" makes the images and the server
 * skips the videos (`skipped`): one toast says so and the page scrolls to the song line.
 */
export function useMakeKitAssets({ accountId, catalogItemUuid }: UseMakeKitAssetsProps): MakeKitAssets {
  const intl = useIntl();
  const { showInfo, showError } = useShowToast();
  const [createKitAssets, { isLoading }] = useCreateKitAssetsMutation();
  const { canSpend, isShort, offerCredits } = useVideoCreditGate(accountId);
  const [skippedVideos, setSkippedVideos] = useState<CreateKitAssetItem[]>(() => readSkipped(catalogItemUuid));
  const keepSkipped = useCallback(
    (items: CreateKitAssetItem[]) => {
      setSkippedVideos(items);
      writeSkipped(catalogItemUuid, items);
    },
    [catalogItemUuid]
  );

  const make = useCallback(
    async (requested: CreateKitAssetItem[], batch: boolean): Promise<void> => {
      if (requested.length === 0) {
        return;
      }
      // "Make the kit" (a batch) always makes the free images; the videos wait when the balance
      // cannot pay for all of them (owner rule 2026-10-06). One asset keeps the plain check.
      const split = batch
        ? splitKitItemsForBalance(requested, isShort)
        : { make: requested, skipped: [], skippedCredits: 0 };
      if (split.skipped.length > 0) {
        keepSkipped(split.skipped);
        offerCredits();
      } else if (batch) {
        keepSkipped([]);
      }
      const items = split.make;
      if (items.length === 0 || !canSpend(itemsCredits(items))) {
        return;
      }
      try {
        const result = await createKitAssets({ accountId, catalogItemUuid, items, batch }).unwrap();
        if (split.skipped.length > 0) {
          showInfo(intl.formatMessage(assetMessages.imagesOnly, { credits: split.skippedCredits }));
          return;
        }
        const skipped = skippedMessage(result);
        if (skipped !== null) {
          showInfo(intl.formatMessage(skipped));
          scrollToSongField();
        } else if (result.busy.length > 0) {
          showInfo(intl.formatMessage(assetMessages.busy));
        }
      } catch (error) {
        const refusal = kitRefusal(error);
        if (refusal === 'videoCredits') {
          return;
        }
        if (refusal === 'busy') {
          showInfo(intl.formatMessage(assetMessages.busy));
          return;
        }
        if (refusal === 'noMaster') {
          showInfo(intl.formatMessage(assetMessages.noMaster));
          scrollToSongField();
          return;
        }
        console.error('Failed to make kit assets', error);
        showError(intl.formatMessage(assetMessages.makeFailed));
      }
    },
    [
      accountId,
      canSpend,
      catalogItemUuid,
      createKitAssets,
      intl,
      isShort,
      keepSkipped,
      offerCredits,
      showError,
      showInfo,
    ]
  );

  const skippedCredits = itemsCredits(skippedVideos);
  const makeSkipped = useCallback(() => {
    if (isShort(skippedCredits)) {
      offerCredits();
      return;
    }
    make(skippedVideos, true).catch(console.error);
  }, [isShort, make, offerCredits, skippedCredits, skippedVideos]);

  return { make, isMaking: isLoading, skippedVideos, skippedCredits, makeSkipped };
}
